/**
 * Live "Interesting Facts" for the login hero. Queries the public
 * `listInterestingFacts` (tenant resolved from Host) via a direct `/graphql`
 * POST — bypassing urql's auth exchange so the login page doesn't trigger a
 * token refresh. Picks the active locale (falling back to English) and returns
 * `null` when there are none (loading, empty tenant, or error).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** A login-hero fact: a short title/tag plus body text. */
export interface AuthFact {
  /** Short category/title shown in the glassmorphism pill. */
  tag: string;
  /** Body copy shown beneath the pill. */
  text: string;
}

/** Backend `InterestingFactLangs` keys (note: no `ky`; has `ar`). */
const LOCALE_KEYS = ['en', 'ru', 'kk', 'ar', 'zh', 'hi', 'id', 'ms', 'fil'] as const;
type LocaleKey = (typeof LOCALE_KEYS)[number];

interface LocaleContent {
  title?: string | null;
  body?: string | null;
}
type FactItem = {
  id: string;
  order: number;
  langs: Partial<Record<LocaleKey, LocaleContent | null>>;
};

const LIST_INTERESTING_FACTS_QUERY = `
  query ListInterestingFacts {
    listInterestingFacts {
      items {
        id
        order
        langs {
          ${LOCALE_KEYS.map((k) => `${k} { title body }`).join('\n          ')}
        }
      }
    }
  }
`;

/** Pick the active locale's complete content, falling back to English. */
function pickContent(langs: FactItem['langs'], lang: string): AuthFact | undefined {
  const key = lang.split('-')[0] as LocaleKey;
  for (const candidate of [langs[key], langs.en]) {
    if (candidate?.title && candidate.body) {
      return { tag: candidate.title, text: candidate.body };
    }
  }
  return undefined;
}

async function fetchInterestingFacts(signal: AbortSignal): Promise<FactItem[]> {
  const res = await fetch('/graphql', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operationName: 'ListInterestingFacts',
      query: LIST_INTERESTING_FACTS_QUERY,
    }),
    signal,
  });
  if (!res.ok) return [];
  const json = (await res.json()) as {
    data?: { listInterestingFacts?: { items?: FactItem[] } };
  };
  return json.data?.listInterestingFacts?.items ?? [];
}

/**
 * Live facts for the current UI language, or `null` when none are available
 * (loading, empty tenant, or fetch error) so callers fall back to static copy.
 */
export function useInterestingFacts(): AuthFact[] | null {
  const { i18n } = useTranslation();
  const [items, setItems] = useState<FactItem[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchInterestingFacts(controller.signal)
      .then((fetched) => {
        setItems(fetched);
      })
      .catch(() => {
        // Network/abort/parse error → leave null; the widget renders nothing.
      });
    return () => {
      controller.abort();
    };
  }, []);

  // DEV-3360: shuffle keyed on `items` so the deck is randomised once per
  // fetch (i.e. per page load) — a language change re-maps to the new locale
  // in the already-shuffled order rather than reshuffling mid-view. Sort by
  // a random key (Schwartzian transform) rather than in-place Fisher-Yates
  // to sidestep `noUncheckedIndexedAccess` swap dance; the deterministic
  // sort on unique random keys is unbiased enough for animation order.
  const shuffled = useMemo(() => {
    if (!items) return null;
    return items
      .map((item) => ({ item, key: Math.random() }))
      .sort((a, b) => a.key - b.key)
      .map(({ item }) => item);
  }, [items]);

  return useMemo(() => {
    if (!shuffled || shuffled.length === 0) return null;
    const facts = shuffled
      .map((item) => pickContent(item.langs, i18n.language))
      .filter((f): f is AuthFact => f !== undefined);
    return facts.length > 0 ? facts : null;
  }, [shuffled, i18n.language]);
}
